import QRCode from 'qrcode'
import { setupUrl } from '../constants'

type PlaybackTransport = 'torrent' | 'direct' | 'hls' | 'torrent/direct' | 'torrent/hls' | 'unknown' | 'torrent/unknown'
const PLAYBACK_TRANSPORTS: PlaybackTransport[] = ['torrent', 'direct', 'hls', 'torrent/direct', 'torrent/hls', 'unknown', 'torrent/unknown']

export interface PlaybackDiagnosticReport {
  v: 1
  createdAt: string
  appVersion: string
  tvModel: string
  runtime: string
  media: string
  source: {
    resolution: string
    codec: string
    audio: string
    container: string
    hdr: string
    transport: PlaybackTransport
  }
  playback: {
    currentTime: number
    duration: number
    bufferedSeconds: number
    downloadSpeed: number
    peers: number
    droppedFrames: number
    totalFrames: number
    error?: string
  }
}

function cleanDeviceText(value: unknown) {
  return typeof value === 'string'
    // Control characters are matched on purpose: this is the strip that keeps
    // them out of a QR payload.
    // eslint-disable-next-line no-control-regex
    ? value.replace(/[\u0000-\u001f\u007f]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 120)
    : ''
}

/** PalmSystem.deviceInfo is a JSON string on installed webOS apps, but some
 * simulator builds expose an object. Parse both without ever placing the full
 * device blob in a diagnostic QR. */
export function webOsDeviceModel(deviceInfo: unknown) {
  let value: unknown = deviceInfo
  if (typeof value === 'string') {
    try { value = JSON.parse(value) as unknown } catch {
      // Some simulators expose a plain model label. Never copy a malformed
      // JSON/device blob into the QR merely because parsing failed.
      const plain = cleanDeviceText(value)
      return /^[a-z0-9][a-z0-9 ._()+-]{1,119}$/i.test(plain) ? plain : ''
    }
  }
  if (!record(value)) return ''
  return cleanDeviceText(value.modelName)
    || cleanDeviceText(value.modelNameAscii)
    || cleanDeviceText(value.model_name)
    || cleanDeviceText(value.model)
}

export function currentTvModel() {
  const palm = (globalThis as typeof globalThis & { PalmSystem?: { deviceInfo?: unknown } }).PalmSystem
  return webOsDeviceModel(palm?.deviceInfo) || 'Unknown LG TV'
}

function diagnosticText(value: unknown, maximum: number, fallback = '', redactUrls = false) {
  const source = typeof value === 'string' ? value : fallback
  return (redactUrls ? source.replace(/https?:\/\/\S+/gi, '[redacted URL]') : source)
    // eslint-disable-next-line no-control-regex -- deliberate: stripped, not matched by accident
    .replace(/[\u0000-\u001f\u007f]/g, ' ').slice(0, maximum)
}

function diagnosticNumber(value: unknown, maximum: number) {
  return typeof value === 'number' && Number.isFinite(value)
    ? Math.max(0, Math.min(maximum, value)) : 0
}

export function sanitizePlaybackDiagnosticReport(report: PlaybackDiagnosticReport): PlaybackDiagnosticReport {
  return {
    v: 1,
    createdAt: diagnosticText(report.createdAt, 64, new Date().toISOString()),
    appVersion: diagnosticText(report.appVersion, 32, 'unknown'),
    tvModel: diagnosticText(report.tvModel, 120, 'Unknown LG TV'),
    runtime: diagnosticText(report.runtime, 300, 'unknown'),
    media: diagnosticText(report.media, 300, 'unknown', true),
    source: {
      resolution: diagnosticText(report.source?.resolution, 40, 'unknown'),
      codec: diagnosticText(report.source?.codec, 40, 'unknown'),
      audio: diagnosticText(report.source?.audio, 40, 'unknown'),
      container: diagnosticText(report.source?.container, 40, 'unknown'),
      hdr: diagnosticText(report.source?.hdr, 40, 'SDR'),
      transport: PLAYBACK_TRANSPORTS.includes(report.source?.transport) ? report.source.transport : 'unknown',
    },
    playback: {
      currentTime: diagnosticNumber(report.playback?.currentTime, 86_400),
      duration: diagnosticNumber(report.playback?.duration, 86_400),
      bufferedSeconds: diagnosticNumber(report.playback?.bufferedSeconds, 86_400),
      downloadSpeed: diagnosticNumber(report.playback?.downloadSpeed, 10_000_000_000),
      peers: diagnosticNumber(report.playback?.peers, 10_000_000),
      droppedFrames: diagnosticNumber(report.playback?.droppedFrames, 10_000_000_000),
      totalFrames: diagnosticNumber(report.playback?.totalFrames, 10_000_000_000),
      error: report.playback?.error === undefined ? undefined : diagnosticText(report.playback.error, 240, '', true),
    },
  }
}

function compactReport(report: PlaybackDiagnosticReport) {
  return btoa(unescape(encodeURIComponent(JSON.stringify(sanitizePlaybackDiagnosticReport(report)))))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function record(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function onlyKeys(value: Record<string, unknown>, allowed: readonly string[]) {
  return Object.keys(value).every((key) => allowed.includes(key))
}

function text(value: unknown, maximum: number) {
  return typeof value === 'string' && value.length <= maximum
}

function number(value: unknown, maximum: number) {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= maximum
}

function isPlaybackDiagnosticReport(value: unknown): value is PlaybackDiagnosticReport {
  if (!record(value) || value.v !== 1 || !text(value.createdAt, 64) || !text(value.appVersion, 32)
    || !text(value.tvModel, 120) || !text(value.runtime, 600) || !text(value.media, 600) || !record(value.source) || !record(value.playback)
    || !onlyKeys(value, ['v', 'createdAt', 'appVersion', 'tvModel', 'runtime', 'media', 'source', 'playback'])
    || !onlyKeys(value.source, ['resolution', 'codec', 'audio', 'container', 'hdr', 'transport'])
    || !onlyKeys(value.playback, ['currentTime', 'duration', 'bufferedSeconds', 'downloadSpeed', 'peers', 'droppedFrames', 'totalFrames', 'error'])) return false
  const source = value.source
  const playback = value.playback
  return text(source.resolution, 40) && text(source.codec, 40) && text(source.audio, 40)
    && text(source.container, 40) && text(source.hdr, 40) && PLAYBACK_TRANSPORTS.includes(source.transport as PlaybackTransport)
    && number(playback.currentTime, 86_400) && number(playback.duration, 86_400)
    && number(playback.bufferedSeconds, 86_400) && number(playback.downloadSpeed, 10_000_000_000)
    && number(playback.peers, 10_000_000) && number(playback.droppedFrames, 10_000_000_000)
    && number(playback.totalFrames, 10_000_000_000)
    && (playback.error === undefined || text(playback.error, 500))
}

export function readDiagnosticPayload(value: string) {
  if (!value || value.length > 8_192) return null
  try {
    const padded = value.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - value.length % 4) % 4)
    const parsed = JSON.parse(decodeURIComponent(escape(atob(padded)))) as unknown
    return isPlaybackDiagnosticReport(parsed) ? parsed : null
  } catch { return null }
}

export function diagnosticUrl(report: PlaybackDiagnosticReport) {
  return `${setupUrl()}?diagnostic=${encodeURIComponent(compactReport(report))}`
}

export async function diagnosticQr(report: PlaybackDiagnosticReport) {
  return QRCode.toDataURL(diagnosticUrl(report), {
    errorCorrectionLevel: 'M',
    margin: 2,
    width: 560,
    color: { dark: '#08090c', light: '#ffffff' },
  })
}
